/**
 * Settings.js - User account settings (profile, password, plan info)
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';
import { getPlanDisplayName } from '../utils/planNames';

const styles = {
  container: {
    maxWidth: 600,
    margin: '0 auto',
    padding: '24px 16px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 32,
  },
  backButton: {
    background: 'none',
    border: 'none',
    fontSize: 16,
    cursor: 'pointer',
    color: 'var(--color-brand)',
    fontWeight: 600,
    padding: '4px 8px',
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: 'var(--color-text)',
    margin: 0,
  },
  section: {
    background: 'var(--color-card)',
    borderRadius: 12,
    padding: 24,
    marginBottom: 20,
    boxShadow: '0 1px 3px var(--color-shadow)',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: 'var(--color-text)',
    margin: '0 0 16px 0',
  },
  fieldGroup: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    border: '1px solid var(--color-input-border)',
    borderRadius: 8,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    background: 'var(--color-input-bg)',
    color: 'var(--color-text)',
  },
  button: {
    background: 'var(--color-brand-gradient)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '10px 24px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    opacity: 1,
    transition: 'opacity 0.2s',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  toast: {
    padding: '10px 16px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 16,
  },
  toastSuccess: {
    background: 'var(--color-toast-success-bg)',
    color: 'var(--color-toast-success-text)',
    border: '1px solid var(--color-toast-success-text)',
  },
  toastError: {
    background: 'var(--color-toast-error-bg)',
    color: 'var(--color-toast-error-text)',
    border: '1px solid var(--color-toast-error-text)',
  },
  planBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'capitalize',
  },
  planInfo: {
    fontSize: 14,
    color: 'var(--color-text-secondary)',
    marginTop: 8,
  },
  planHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  planNameGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  planTierLabel: {
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--color-text)',
    margin: 0,
  },
  planTagline: {
    fontSize: 14,
    color: 'var(--color-text-secondary)',
    margin: '4px 0 0 0',
  },
  usageSection: {
    background: 'var(--color-section-bg)',
    borderRadius: 10,
    padding: '16px 20px',
    marginBottom: 20,
  },
  usageTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 12px 0',
  },
  usageRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  usageLabel: {
    fontSize: 14,
    color: 'var(--color-text-secondary)',
  },
  usageValue: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--color-text)',
  },
  usageBarTrack: {
    height: 6,
    borderRadius: 3,
    background: 'var(--color-border)',
    marginTop: 4,
  },
  featuresSection: {
    marginBottom: 20,
  },
  featuresTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 12px 0',
  },
  featureRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 0',
    fontSize: 14,
    color: 'var(--color-text)',
  },
  featureCheck: {
    color: '#22c55e',
    fontWeight: 700,
    fontSize: 15,
    flexShrink: 0,
    width: 20,
    textAlign: 'center',
  },
  featureLock: {
    color: 'var(--color-border)',
    fontWeight: 700,
    fontSize: 15,
    flexShrink: 0,
    width: 20,
    textAlign: 'center',
  },
  featureTextLocked: {
    color: 'var(--color-text-secondary)',
  },
  divider: {
    height: 1,
    background: 'var(--color-border)',
    margin: '20px 0',
    border: 'none',
  },
  managementSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  managementButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'var(--color-card)',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    padding: '12px 16px',
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--color-text)',
    cursor: 'pointer',
    transition: 'background 0.15s, border-color 0.15s',
  },
  managementArrow: {
    color: 'var(--color-text-secondary)',
    fontSize: 16,
  },
  stripeStatusBox: {
    borderRadius: 10,
    padding: '16px 20px',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
  },
  stripeStatusIcon: {
    fontSize: 22,
    lineHeight: 1,
    flexShrink: 0,
    marginTop: 2,
  },
  stripeStatusContent: {
    flex: 1,
  },
  stripeStatusTitle: {
    fontSize: 15,
    fontWeight: 600,
    margin: '0 0 4px 0',
  },
  stripeStatusDesc: {
    fontSize: 13,
    color: 'var(--color-text-secondary)',
    margin: '0 0 12px 0',
    lineHeight: 1.5,
  },
  stripeDetailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    color: 'var(--color-text-secondary)',
    marginBottom: 4,
  },
  stripeConnectButton: {
    background: '#635BFF',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  stripeDashboardButton: {
    background: 'var(--color-card)',
    color: '#635BFF',
    border: '1px solid #635BFF',
    borderRadius: 8,
    padding: '9px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
};

const planColors = {
  pro: { background: '#f0e6ff', color: '#6b21a8' },
  starter: { background: '#e0edff', color: '#1e40af' },
  free: { background: '#f0f0f0', color: '#666' },
};

const PLAN_TAGLINES = {
  free: 'Just getting started. Perfect for your first league.',
  starter: 'Everything you need to run a great season.',
  pro: 'For the organizer who runs it all.',
};

const PLAN_LIMITS = {
  free: { leagues: 1, teams: 8 },
  starter: { leagues: 1, teams: 'Unlimited' },
  pro: { leagues: 'Unlimited', teams: 'Unlimited' },
};

const PLAN_FEATURES = [
  { label: 'Create leagues', plans: ['free', 'starter', 'pro'] },
  { label: 'Schedule generation', plans: ['free', 'starter', 'pro'] },
  { label: 'Public league page', plans: ['free', 'starter', 'pro'] },
  { label: 'PWA support', plans: ['free', 'starter', 'pro'] },
  { label: 'PDF export (clean, no watermark)', plans: ['starter', 'pro'] },
  { label: 'Email notifications', plans: ['starter', 'pro'] },
  { label: 'Captain portal', plans: ['starter', 'pro'] },
  { label: 'Venue management', plans: ['starter', 'pro'] },
  { label: 'Unlimited teams per league', plans: ['starter', 'pro'] },
  { label: 'Score entry & standings', plans: ['pro'] },
  { label: 'Captain & referee score submission', plans: ['pro'] },
  { label: 'Playoff brackets', plans: ['pro'] },
  { label: 'Season archiving', plans: ['pro'] },
  { label: 'Referee management', plans: ['pro'] },
  { label: 'Unlimited leagues', plans: ['pro'] },
];

function Settings({ onBack }) {
  const { user, refreshUser } = useAuth();

  // Profile form
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileToast, setProfileToast] = useState(null);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordToast, setPasswordToast] = useState(null);

  // Plan usage stats
  const [activeLeagueCount, setActiveLeagueCount] = useState(null);

  // Subscription management state
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(null); // which plan is loading
  const [portalLoading, setPortalLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [downgradeCheck, setDowngradeCheck] = useState(null); // { canDowngrade, issues }
  const [subscriptionToast, setSubscriptionToast] = useState(null);

  // Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState(null); // null = loading
  const [stripeLoading, setStripeLoading] = useState(true);
  const [stripeConnecting, setStripeConnecting] = useState(false);
  const [stripeDashboardLoading, setStripeDashboardLoading] = useState(false);
  const [stripeToast, setStripeToast] = useState(null);

  useEffect(() => {
    api.getLeagues().then(leagues => {
      const active = leagues.filter(l => l.status !== 'completed').length;
      setActiveLeagueCount(active);
    }).catch(() => setActiveLeagueCount(0));

    api.getStripeStatus().then(status => {
      setStripeStatus(status);
    }).catch(() => {
      setStripeStatus({ connected: false, onboardingComplete: false, chargesEnabled: false, payoutsEnabled: false });
    }).finally(() => setStripeLoading(false));

    api.getSubscriptionDetails().then(details => {
      setSubscriptionDetails(details);
    }).catch(() => {
      setSubscriptionDetails(null);
    }).finally(() => setSubscriptionLoading(false));

    // Check for checkout result from URL params
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      setSubscriptionToast({ message: 'Subscription activated! Welcome to your new plan.', type: 'success' });
      refreshUser();
      // Clean the URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('checkout') === 'canceled') {
      setSubscriptionToast({ message: 'Checkout was canceled.', type: 'error' });
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showStripeToast = (message, type) => {
    setStripeToast({ message, type });
    setTimeout(() => setStripeToast(null), 5000);
  };

  const handleConnectStripe = async () => {
    setStripeConnecting(true);
    try {
      const { url } = await api.connectStripe();
      window.location.href = url;
    } catch (err) {
      showStripeToast(err.message, 'error');
      setStripeConnecting(false);
    }
  };

  const handleOpenStripeDashboard = async () => {
    setStripeDashboardLoading(true);
    try {
      const { url } = await api.getStripeDashboardLink();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      showStripeToast(err.message, 'error');
    } finally {
      setStripeDashboardLoading(false);
    }
  };

  const showSubscriptionToast = (message, type) => {
    setSubscriptionToast({ message, type });
    setTimeout(() => setSubscriptionToast(null), 5000);
  };

  const handleUpgrade = async (targetPlan) => {
    setUpgradeLoading(targetPlan);
    try {
      // If user already has a Stripe subscription, use portal
      if (subscriptionDetails?.stripeSubscriptionId) {
        const { url } = await api.createPortalSession();
        window.location.href = url;
        return;
      }
      const { url } = await api.createSubscriptionCheckout(targetPlan);
      window.location.href = url;
    } catch (err) {
      showSubscriptionToast(err.message, 'error');
      setUpgradeLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { url } = await api.createPortalSession();
      window.location.href = url;
    } catch (err) {
      showSubscriptionToast(err.message, 'error');
      setPortalLoading(false);
    }
  };

  const handleCheckDowngrade = async (targetPlan) => {
    try {
      const result = await api.checkDowngrade(targetPlan);
      setDowngradeCheck(result);
      if (result.canDowngrade) {
        // Safe to downgrade — open portal
        const { url } = await api.createPortalSession();
        window.location.href = url;
      }
    } catch (err) {
      showSubscriptionToast(err.message, 'error');
    }
  };

  const handleReactivate = async () => {
    setReactivateLoading(true);
    try {
      await api.reactivateSubscription();
      showSubscriptionToast('Subscription reactivated!', 'success');
      // Refresh subscription details
      const details = await api.getSubscriptionDetails();
      setSubscriptionDetails(details);
      await refreshUser();
    } catch (err) {
      showSubscriptionToast(err.message, 'error');
    } finally {
      setReactivateLoading(false);
    }
  };

  const showProfileToast = (message, type) => {
    setProfileToast({ message, type });
    setTimeout(() => setProfileToast(null), 4000);
  };

  const showPasswordToast = (message, type) => {
    setPasswordToast({ message, type });
    setTimeout(() => setPasswordToast(null), 4000);
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      showProfileToast('Name cannot be empty', 'error');
      return;
    }
    setProfileSaving(true);
    try {
      await api.updateProfile({ name: name.trim(), email: email.trim() });
      await refreshUser();
      showProfileToast('Profile updated', 'success');
    } catch (err) {
      showProfileToast(err.message, 'error');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showPasswordToast('New passwords do not match', 'error');
      return;
    }
    setPasswordSaving(true);
    try {
      await api.changePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showPasswordToast('Password changed', 'success');
    } catch (err) {
      showPasswordToast(err.message, 'error');
    } finally {
      setPasswordSaving(false);
    }
  };

  const plan = user?.plan || 'free';
  const badgeColors = planColors[plan] || planColors.free;
  const limits = PLAN_LIMITS[plan] || PLAN_LIMITS.free;

  const profileChanged = name.trim() !== (user?.name || '') || email.trim() !== (user?.email || '');

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={onBack}>&larr; Back</button>
        <h1 style={styles.title}>Account Settings</h1>
      </div>

      {/* Profile Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Profile</h2>
        {profileToast && (
          <div style={{ ...styles.toast, ...(profileToast.type === 'success' ? styles.toastSuccess : styles.toastError) }}>
            {profileToast.message}
          </div>
        )}
        <form onSubmit={handleProfileSave}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Name</label>
            <input
              style={styles.input}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Email</label>
            <input
              style={styles.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <button
            type="submit"
            style={{ ...styles.button, ...(profileSaving || !profileChanged ? styles.buttonDisabled : {}) }}
            disabled={profileSaving || !profileChanged}
          >
            {profileSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* Password Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Change Password</h2>
        {passwordToast && (
          <div style={{ ...styles.toast, ...(passwordToast.type === 'success' ? styles.toastSuccess : styles.toastError) }}>
            {passwordToast.message}
          </div>
        )}
        <form onSubmit={handlePasswordChange}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Current Password</label>
            <input
              style={styles.input}
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>New Password</label>
            <input
              style={styles.input}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={6}
            />
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Confirm New Password</label>
            <input
              style={styles.input}
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={6}
            />
          </div>
          <button
            type="submit"
            style={{ ...styles.button, ...(passwordSaving || !currentPassword || !newPassword || !confirmPassword ? styles.buttonDisabled : {}) }}
            disabled={passwordSaving || !currentPassword || !newPassword || !confirmPassword}
          >
            {passwordSaving ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Plan Section */}
      <div style={styles.section}>
        {/* Plan header with name + badge */}
        <div style={styles.planHeader}>
          <div>
            <div style={styles.planNameGroup}>
              <h2 style={{ ...styles.planTierLabel, margin: 0 }}>{getPlanDisplayName(plan)}</h2>
              <span style={{ ...styles.planBadge, ...badgeColors }}>
                {plan === 'free' ? 'Free' : subscriptionDetails?.cancelAtPeriodEnd ? 'Canceling' : 'Active'}
              </span>
            </div>
            <p style={styles.planTagline}>{PLAN_TAGLINES[plan]}</p>
          </div>
        </div>

        {subscriptionToast && (
          <div style={{ ...styles.toast, ...(subscriptionToast.type === 'success' ? styles.toastSuccess : styles.toastError) }}>
            {subscriptionToast.message}
          </div>
        )}

        {/* Cancellation notice */}
        {subscriptionDetails?.cancelAtPeriodEnd && subscriptionDetails?.currentPeriodEnd && (
          <div style={{
            background: 'var(--color-badge-draft-bg)',
            border: '1px solid var(--color-badge-draft-text)',
            borderRadius: 10,
            padding: '14px 18px',
            marginBottom: 16,
            fontSize: 14,
            color: 'var(--color-badge-draft-text)',
            lineHeight: 1.5,
          }}>
            Your subscription is set to cancel on{' '}
            <strong>{new Date(subscriptionDetails.currentPeriodEnd).toLocaleDateString()}</strong>.
            You'll keep your current plan features until then.
            <button
              style={{
                display: 'block',
                marginTop: 10,
                background: 'var(--color-brand-gradient)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                opacity: reactivateLoading ? 0.6 : 1,
              }}
              onClick={handleReactivate}
              disabled={reactivateLoading}
            >
              {reactivateLoading ? 'Reactivating...' : 'Keep My Subscription'}
            </button>
          </div>
        )}

        {/* Billing info */}
        {!subscriptionLoading && subscriptionDetails?.currentPeriodEnd && plan !== 'free' && !subscriptionDetails?.cancelAtPeriodEnd && (
          <div style={{ ...styles.usageSection, marginBottom: 16 }}>
            <h3 style={styles.usageTitle}>Billing</h3>
            <div style={styles.usageRow}>
              <span style={styles.usageLabel}>Next billing date</span>
              <span style={styles.usageValue}>
                {new Date(subscriptionDetails.currentPeriodEnd).toLocaleDateString()}
              </span>
            </div>
          </div>
        )}

        {/* Usage stats */}
        <div style={styles.usageSection}>
          <h3 style={styles.usageTitle}>Usage</h3>
          <div style={styles.usageRow}>
            <span style={styles.usageLabel}>Active leagues</span>
            <span style={styles.usageValue}>
              {activeLeagueCount !== null ? (
                limits.leagues === 'Unlimited'
                  ? activeLeagueCount
                  : `${activeLeagueCount} of ${limits.leagues}`
              ) : '...'}
            </span>
          </div>
          {limits.leagues !== 'Unlimited' && activeLeagueCount !== null && (
            <div style={styles.usageBarTrack}>
              <div style={{
                height: '100%',
                borderRadius: 3,
                background: activeLeagueCount >= limits.leagues
                  ? '#ef4444'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                width: `${Math.min((activeLeagueCount / limits.leagues) * 100, 100)}%`,
                transition: 'width 0.3s ease',
              }} />
            </div>
          )}

          <div style={{ ...styles.usageRow, marginTop: 12 }}>
            <span style={styles.usageLabel}>Teams per league</span>
            <span style={styles.usageValue}>
              {limits.teams === 'Unlimited' ? 'Unlimited' : `Up to ${limits.teams}`}
            </span>
          </div>

          <div style={{ ...styles.usageRow, marginTop: 12 }}>
            <span style={styles.usageLabel}>PDF export</span>
            <span style={styles.usageValue}>
              {plan === 'free' ? 'Draft watermark' : 'Clean export'}
            </span>
          </div>
        </div>

        {/* Features for current plan */}
        <div style={styles.featuresSection}>
          <h3 style={styles.featuresTitle}>Plan Features</h3>
          {PLAN_FEATURES.map((feature, i) => {
            const included = feature.plans.includes(plan);
            return (
              <div key={i} style={styles.featureRow}>
                <span style={included ? styles.featureCheck : styles.featureLock}>
                  {included ? '\u2713' : '\u2014'}
                </span>
                <span style={included ? {} : styles.featureTextLocked}>{feature.label}</span>
              </div>
            );
          })}
        </div>

        <hr style={styles.divider} />

        {/* Management buttons */}
        <div style={styles.managementSection}>
          {/* Upgrade buttons */}
          {plan === 'free' && (
            <>
              <button
                style={{ ...styles.managementButton, ...(upgradeLoading === 'starter' ? styles.buttonDisabled : {}) }}
                onClick={() => handleUpgrade('starter')}
                disabled={!!upgradeLoading}
                onMouseEnter={e => { if (!upgradeLoading) { e.currentTarget.style.background = 'var(--color-card-hover)'; e.currentTarget.style.borderColor = 'var(--color-brand)'; } }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-card)'; e.currentTarget.style.borderColor = 'var(--color-border)'; }}
              >
                <span>{upgradeLoading === 'starter' ? 'Redirecting to checkout...' : `Upgrade to ${getPlanDisplayName('starter')}`}</span>
                <span style={styles.managementArrow}>&rarr;</span>
              </button>
              <button
                style={{ ...styles.managementButton, ...(upgradeLoading === 'pro' ? styles.buttonDisabled : {}) }}
                onClick={() => handleUpgrade('pro')}
                disabled={!!upgradeLoading}
                onMouseEnter={e => { if (!upgradeLoading) { e.currentTarget.style.background = 'var(--color-card-hover)'; e.currentTarget.style.borderColor = 'var(--color-brand)'; } }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-card)'; e.currentTarget.style.borderColor = 'var(--color-border)'; }}
              >
                <span>{upgradeLoading === 'pro' ? 'Redirecting to checkout...' : `Upgrade to ${getPlanDisplayName('pro')}`}</span>
                <span style={styles.managementArrow}>&rarr;</span>
              </button>
            </>
          )}
          {plan === 'starter' && (
            <button
              style={{ ...styles.managementButton, ...(upgradeLoading === 'pro' ? styles.buttonDisabled : {}) }}
              onClick={() => handleUpgrade('pro')}
              disabled={!!upgradeLoading}
              onMouseEnter={e => { if (!upgradeLoading) { e.currentTarget.style.background = 'var(--color-card-hover)'; e.currentTarget.style.borderColor = 'var(--color-brand)'; } }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-card)'; e.currentTarget.style.borderColor = 'var(--color-border)'; }}
            >
              <span>{upgradeLoading === 'pro' ? 'Redirecting...' : `Upgrade to ${getPlanDisplayName('pro')}`}</span>
              <span style={styles.managementArrow}>&rarr;</span>
            </button>
          )}

          {/* Manage subscription (portal) for paid plans */}
          {plan !== 'free' && subscriptionDetails?.stripeSubscriptionId && (
            <button
              style={{ ...styles.managementButton, ...(portalLoading ? styles.buttonDisabled : {}) }}
              onClick={handleManageSubscription}
              disabled={portalLoading}
              onMouseEnter={e => { if (!portalLoading) { e.currentTarget.style.background = 'var(--color-card-hover)'; e.currentTarget.style.borderColor = 'var(--color-brand)'; } }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-card)'; e.currentTarget.style.borderColor = 'var(--color-border)'; }}
            >
              <span>{portalLoading ? 'Opening portal...' : 'Manage Subscription'}</span>
              <span style={styles.managementArrow}>&rarr;</span>
            </button>
          )}

          {/* Downgrade check for paid plans */}
          {plan === 'pro' && subscriptionDetails?.stripeSubscriptionId && (
            <button
              style={styles.managementButton}
              onClick={() => handleCheckDowngrade('starter')}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-card-hover)'; e.currentTarget.style.borderColor = 'var(--color-brand)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-card)'; e.currentTarget.style.borderColor = 'var(--color-border)'; }}
            >
              <span>Downgrade to {getPlanDisplayName('starter')}</span>
              <span style={styles.managementArrow}>&rarr;</span>
            </button>
          )}
          {plan !== 'free' && subscriptionDetails?.stripeSubscriptionId && (
            <button
              style={styles.managementButton}
              onClick={() => handleCheckDowngrade('free')}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-card-hover)'; e.currentTarget.style.borderColor = 'var(--color-brand)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-card)'; e.currentTarget.style.borderColor = 'var(--color-border)'; }}
            >
              <span>Cancel Subscription</span>
              <span style={styles.managementArrow}>&rarr;</span>
            </button>
          )}

          {/* For paid users without Stripe subscription (admin-set plans) */}
          {plan !== 'free' && !subscriptionDetails?.stripeSubscriptionId && !subscriptionLoading && (
            <div style={{
              fontSize: 13,
              color: 'var(--color-text-secondary)',
              padding: '8px 0',
              lineHeight: 1.5,
            }}>
              Your plan was set by an administrator. Contact{' '}
              <a href="mailto:support@leaguemaker.app" style={{ color: 'var(--color-brand)', fontWeight: 600 }}>
                support@leaguemaker.app
              </a>{' '}
              to make changes.
            </div>
          )}
        </div>

        {/* Downgrade guardrail warning */}
        {downgradeCheck && !downgradeCheck.canDowngrade && (
          <div style={{
            background: 'var(--color-toast-error-bg)',
            border: '1px solid var(--color-toast-error-text)',
            borderRadius: 10,
            padding: '14px 18px',
            marginTop: 12,
            fontSize: 14,
            color: 'var(--color-toast-error-text)',
            lineHeight: 1.5,
          }}>
            <strong>Cannot downgrade yet:</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
              {downgradeCheck.issues.map((issue, i) => (
                <li key={i}>{issue.message}</li>
              ))}
            </ul>
            <button
              style={{
                display: 'block',
                marginTop: 10,
                background: 'none',
                border: 'none',
                color: 'var(--color-toast-error-text)',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
              onClick={() => setDowngradeCheck(null)}
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      {/* Payments / Stripe Connect Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Payments</h2>

        {stripeToast && (
          <div style={{ ...styles.toast, ...(stripeToast.type === 'success' ? styles.toastSuccess : styles.toastError) }}>
            {stripeToast.message}
          </div>
        )}

        {stripeLoading ? (
          <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', margin: 0 }}>Checking Stripe status...</p>
        ) : stripeStatus?.chargesEnabled ? (
          /* Fully connected and active */
          <div style={{ ...styles.stripeStatusBox, background: 'var(--color-toast-success-bg)', border: '1px solid var(--color-toast-success-text)' }}>
            <span style={styles.stripeStatusIcon}>&#x2705;</span>
            <div style={styles.stripeStatusContent}>
              <p style={{ ...styles.stripeStatusTitle, color: 'var(--color-toast-success-text)' }}>Stripe account connected</p>
              <div style={styles.stripeDetailRow}>
                <span style={{ color: '#22c55e' }}>&#x2713;</span>
                <span>Charges {stripeStatus.chargesEnabled ? 'enabled' : 'disabled'}</span>
              </div>
              <div style={{ ...styles.stripeDetailRow, marginBottom: 14 }}>
                <span style={{ color: '#22c55e' }}>&#x2713;</span>
                <span>Payouts {stripeStatus.payoutsEnabled ? 'enabled' : 'disabled'}</span>
              </div>
              <button
                style={{ ...styles.stripeDashboardButton, opacity: stripeDashboardLoading ? 0.7 : 1 }}
                onClick={handleOpenStripeDashboard}
                disabled={stripeDashboardLoading}
              >
                {stripeDashboardLoading ? 'Opening...' : 'Open Stripe Dashboard \u2197'}
              </button>
            </div>
          </div>
        ) : stripeStatus?.connected ? (
          /* Connected but onboarding incomplete */
          <div style={{ ...styles.stripeStatusBox, background: 'var(--color-badge-draft-bg)', border: '1px solid var(--color-badge-draft-text)' }}>
            <span style={styles.stripeStatusIcon}>&#x26A0;&#xFE0F;</span>
            <div style={styles.stripeStatusContent}>
              <p style={{ ...styles.stripeStatusTitle, color: 'var(--color-badge-draft-text)' }}>Setup incomplete</p>
              <p style={styles.stripeStatusDesc}>
                Your Stripe account is connected but onboarding is not finished. Complete setup to enable payment collection.
              </p>
              <button
                style={{ ...styles.stripeConnectButton, opacity: stripeConnecting ? 0.7 : 1 }}
                onClick={handleConnectStripe}
                disabled={stripeConnecting}
              >
                {stripeConnecting ? 'Redirecting...' : 'Complete Stripe Setup \u2192'}
              </button>
            </div>
          </div>
        ) : (
          /* Not connected */
          <div style={{ ...styles.stripeStatusBox, background: 'var(--color-section-bg)', border: '1px solid var(--color-border)' }}>
            <span style={styles.stripeStatusIcon}>&#x1F4B3;</span>
            <div style={styles.stripeStatusContent}>
              <p style={{ ...styles.stripeStatusTitle, color: 'var(--color-text)' }}>Connect a Stripe account</p>
              <p style={styles.stripeStatusDesc}>
                Accept registration fees directly through your Stripe account. Payouts go straight to you — LeagueMaker charges a 1% platform fee.
              </p>
              <button
                style={{ ...styles.stripeConnectButton, opacity: stripeConnecting ? 0.7 : 1 }}
                onClick={handleConnectStripe}
                disabled={stripeConnecting}
              >
                {stripeConnecting ? 'Redirecting to Stripe...' : 'Connect Stripe Account \u2192'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Settings;
